import { useState } from 'react'
import type { User } from '../lib/auth'
import Account from '../components/Account'
import Admin from '../components/Admin'
import SignOut from '../components/SignOut'
import DeleteAccount from './DeleteAccount'
import Feedback from './Feedback'
import ProfileBuildInfo from './ProfileBuildInfo'
import WorxAdmin from './WorxAdmin'
import { tw } from './strings'
import { AddRole, FindProCard } from './roles'

/**
 * The admin console, at the bottom of whichever account screen somebody is on.
 *
 * It used to be one small circle in the app bar, which nobody looked for and
 * which does not exist on the opening screen. Everyone has an account here —
 * including people only hiring a plumber — so the account screen is where an
 * administrator looks, and the console appears there only for someone holding
 * `admin.access`. Everyone else gets nothing, not a disabled control.
 */
export function AdminSection({ user }: { user: User }) {
  // Core's console is raw tables (users, flags, errors, keys). Useful, but it is
  // the long part, so it starts closed and the things an administrator reaches
  // for daily — the command line and the queues — are first.
  const [core, setCore] = useState(false)
  if (!user.capabilities.includes('admin.access')) return null
  return (
    <section className="w-admin-section" aria-label={tw('w.admin.section')}>
      <h2>{tw('w.admin.section')}</h2>

      <p className="muted small">
        Type <code>help</code> in the command line for everything it can do. Useful ones:{' '}
        <code>pending</code>, <code>preferred global on</code>, <code>preferred on &lt;ad title&gt;</code>,{' '}
        <code>remove service &lt;ad&gt;: &lt;reason&gt;</code>.
      </p>

      {/* Beside core's console, never inside it — see WorxAdmin.tsx. */}
      <WorxAdmin capabilities={user.capabilities} commandLine="top" />

      <button className="w-admin-core-toggle" aria-expanded={core} onClick={() => setCore((v) => !v)}>
        {core ? 'Hide' : 'Show'} users, flags, errors and keys
      </button>
      {core && <Admin />}
    </section>
  )
}

/** Passkeys and devices, sign out, delete, the build stamp, and (for admins) the console. */
export default function AccountPanel({
  user, onSignedOut, environment,
}: {
  user: User
  onSignedOut: () => void | Promise<void>
  environment?: string
}) {
  return (
    <>
      <div className="w-profile-account"><Account user={user} /></div>
      {/* Feedback used to be a tab of its own. Five tabs is what lets the main
          one sit in the middle with the others balanced either side, so it
          lives here, one tap from every side's account screen. */}
      <details className="w-feedback-fold">
        <summary>{tw('w.fb.title')}</summary>
        <Feedback />
      </details>
      <FindProCard />
      <AddRole />
      <SignOut onSignedOut={onSignedOut} />
      <DeleteAccount onDeleted={onSignedOut} />
      <ProfileBuildInfo environment={environment} />
      <AdminSection user={user} />
    </>
  )
}
